import {
  ConfigurationTable,
  ConfigurationTableParams,
} from "@plansoft-configuration-renderer/configuration-api";
import {
  useConfigurationApi,
  useConfigurationCurrency,
  useConfigurationLanguage,
  useCsAttribute,
  useSessionId,
} from "@plansoft-configuration-renderer/framework";
import {
  UseQueryResult,
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

type ConfigurationTableInjectedParams = Pick<
  ConfigurationTableParams,
  "localeId" | "objectPath" | "attributeId" | "id" | "currencyId"
>;

type UseTableControlParams = Omit<
  ConfigurationTableParams,
  keyof ConfigurationTableInjectedParams
>;

const BASE_QUERY_KEY = "configurationApi.getTable";

/**
 *
 * @param options The options for the table control
 * @param initialData Initial data for server side mode
 * @returns
 */
export function useTableControlData(
  options: UseTableControlParams,
): UseQueryResult<ConfigurationTable> {
  const api = useConfigurationApi();

  // dependencies for injected options
  const sessionId = useSessionId();
  const localeId = useConfigurationLanguage();
  const currencyId = useConfigurationCurrency();
  const attribute = useCsAttribute();

  // this token is set in the backend configuration script to detect changes in the table
  const tableChangeToken = attribute.tableChangeToken;

  const injectedParams: ConfigurationTableInjectedParams = {
    id: attribute.tableId as string, // must be checked before
    localeId,
    attributeId: attribute.id,
    currencyId,
    objectPath: attribute.path.slice(0, attribute.path.lastIndexOf("/") + 1),
  };

  const client = useQueryClient();

  const result = useQuery({
    queryKey: [
      BASE_QUERY_KEY,
      tableChangeToken,
      options,
      injectedParams,
      sessionId,
    ],
    queryFn: async () => {
      const table = await api.getTable({
        id: sessionId,
        configurationTableParams: {
          ...options,
          ...injectedParams,
        },
      });

      // detect initial fetch, persist defaults from the server to the query cache
      if (options.filters == null || options.orderings == null) {
        const updatedOptions = { ...options };

        // expect the backend to return default values for filters
        if (updatedOptions.filters == null) {
          updatedOptions.filters = table.filters ?? [];
        }

        // expect the backend has default values for sorting
        if (updatedOptions.orderings == null) {
          updatedOptions.orderings = table.orderings ?? [];
        }

        // persist defaults from the server to the query cache to prevent additional fetches
        client.setQueryData(
          [
            BASE_QUERY_KEY,
            tableChangeToken,
            updatedOptions,
            injectedParams,
            sessionId,
          ],
          table,
        );
      }

      return table;
    },

    placeholderData: keepPreviousData, // this prevents the table from flickering when the table is refetched
    refetchOnWindowFocus: false, // disable default refetch, this is very annoying in development
    refetchOnMount: false, // for server filtering, we mount the hook in several place. this prevents refetch on mount
  });

  return result;
}
